import {
  ArrowDownTrayIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
} from '@heroicons/react/24/outline';
import { PauseIcon, PlayIcon } from '@heroicons/react/24/solid';
import { Box, IconButton, Popover, Slider, Tooltip } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import useThemeStore from 'Theme/store';
import { isIOS } from 'utils/device';

const AudioPlayer = ({ audioUrl, allowDonwload = true, actions = [] }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isSeeking, setIsSeeking] = useState(false);
  const [anchorElVolume, setAnchorElVolume] = useState(null);
  const [lastVolume, setLastVolume] = useState(1);

  const { theme } = useThemeStore();
  const isIOSDevice = isIOS();

  useEffect(() => {
    const audio = audioRef.current;

    const setAudioData = () => {
      setDuration(audio.duration);
    };

    const setAudioTime = () => {
      if (!isSeeking) {
        setCurrentTime(audio.currentTime);
      }
    };

    const setAudioPlayState = () => {
      setIsPlaying(!audio.paused);
    };

    const updateVolumeState = () => {
      setVolume(audio.volume);
    };

    audio.addEventListener('loadeddata', setAudioData);
    audio.addEventListener('timeupdate', setAudioTime);
    audio.addEventListener('play', setAudioPlayState);
    audio.addEventListener('pause', setAudioPlayState);
    audio.addEventListener('ended', setAudioPlayState);
    audio.addEventListener('volumechange', updateVolumeState);

    return () => {
      audio.removeEventListener('loadeddata', setAudioData);
      audio.removeEventListener('timeupdate', setAudioTime);
      audio.removeEventListener('play', setAudioPlayState);
      audio.removeEventListener('pause', setAudioPlayState);
      audio.removeEventListener('ended', setAudioPlayState);
      audio.removeEventListener('volumechange', updateVolumeState);
    };
  }, [isSeeking]);

  const togglePlayPause = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  };

  const handleVolumeChange = (_, newValue) => {
    const audio = audioRef.current;
    audio.volume = newValue;
    if (newValue > 0 && volume === 0) {
      setLastVolume(newValue);
    }
  };

  const handleSeekChange = (_, newValue) => {
    setIsSeeking(true);
    setCurrentTime(newValue);
  };

  const handleSeekCommitted = (_, newValue) => {
    const audio = audioRef.current;
    audio.currentTime = newValue;
    setIsSeeking(false);
  };

  const handleVolumeIconClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorElVolume(anchorElVolume ? null : event.currentTarget);
  };

  const handleVolumeIconDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const audio = audioRef.current;
    if (audio.volume > 0) {
      setLastVolume(audio.volume);
      audio.volume = 0;
    } else {
      audio.volume = lastVolume > 0 ? lastVolume : 1;
    }
    setAnchorElVolume(null);
  };

  const handleVolumePopoverClose = () => {
    setAnchorElVolume(null);
  };

  const handleDownloadClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const link = document.createElement('a');
    link.href = audioUrl;
    const urlParts = audioUrl.split('/');
    const filename = urlParts[urlParts.length - 1];
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const openVolumePopover = Boolean(anchorElVolume);
  const volumePopoverId = openVolumePopover ? 'volume-popover' : undefined;

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <div
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      className="flex w-full flex-col sm:flex-row items-center gap-3 sm:gap-3"
    >
      <div className="font-figtree flex-1 flex flex-col sm:flex-row items-center px-3 py-3 sm:py-2 gap-3 sm:gap-3 bg-platinum dark:bg-tornado-cloud rounded-2xl sm:rounded-3xl w-full">
        {/* Top row: Time display and slider */}
        <div className="flex items-center gap-2 w-full sm:flex-1">
          <div className="font-semibold text-xs leading-4 text-agate-violet dark:text-lavender min-w-[50px] sm:min-w-auto">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>

          <Slider
            value={currentTime}
            max={duration}
            onChange={handleSeekChange}
            onChangeCommitted={handleSeekCommitted}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            sx={{
              height: 6,
              color: theme === 'dark' ? '#D4D5E6' : '#51526C',
              padding: '20px 0 !important',
              flex: '1 1 0%',
              '@media (min-width: 640px)': {
                height: 4,
                padding: '15px 0 !important',
              },
              '& .MuiSlider-thumb': {
                width: 16,
                height: 16,
                bgcolor: theme === 'dark' ? '#D4D5E6' : '#51526C',
                '@media (min-width: 640px)': {
                  width: 12,
                  height: 12,
                },
                '&:before': {
                  boxShadow: '0 2px 12px 0 rgba(0,0,0,0.2)',
                },
                '&:hover, &.Mui-focusVisible, &.Mui-active': {
                  boxShadow: '0px 0px 0px 8px rgba(81, 82, 108, 0.16)',
                },
              },
              '& .MuiSlider-rail': {
                bgcolor: '#B5B6C8',
              },
              '& .MuiSlider-track': {
                height: 6,
                '@media (min-width: 640px)': {
                  height: 4,
                },
              },
            }}
          />
        </div>

        {/* Bottom row: Control buttons */}
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto justify-center sm:justify-start">
          <IconButton
            onClick={togglePlayPause}
            sx={{
              minWidth: '48px',
              minHeight: '48px',
              '@media (min-width: 640px)': {
                minWidth: '40px',
                minHeight: '40px',
              },
            }}
          >
            {isPlaying ? (
              <PauseIcon className="h-6 w-6 sm:h-5 sm:w-5 text-agate-violet dark:text-lavender" />
            ) : (
              <PlayIcon className="h-6 w-6 sm:h-5 sm:w-5 text-agate-violet dark:text-lavender" />
            )}
          </IconButton>

          {!isIOSDevice && (
            <IconButton
              onClick={handleVolumeIconClick}
              onDoubleClick={handleVolumeIconDoubleClick}
              sx={{
                minWidth: '48px',
                minHeight: '48px',
                '@media (min-width: 640px)': {
                  minWidth: '40px',
                  minHeight: '40px',
                },
              }}
            >
              {volume > 0 ? (
                <SpeakerWaveIcon className="h-6 w-6 sm:h-5 sm:w-5 text-agate-violet dark:text-lavender" />
              ) : (
                <SpeakerXMarkIcon className="h-6 w-6 sm:h-5 sm:w-5 text-agate-violet dark:text-lavender" />
              )}
            </IconButton>
          )}

          {allowDonwload && (
            <Tooltip
              title="Download"
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: '14px',
                    fontFamily: 'Figtree',
                  },
                },
              }}
            >
              <IconButton
                onClick={handleDownloadClick}
                sx={{
                  minWidth: '48px',
                  minHeight: '48px',
                  '@media (min-width: 640px)': {
                    minWidth: '40px',
                    minHeight: '40px',
                  },
                }}
              >
                <ArrowDownTrayIcon className="h-6 w-6 sm:h-5 sm:w-5 text-agate-violet dark:text-lavender" />
              </IconButton>
            </Tooltip>
          )}

          {actions &&
            actions.length > 0 &&
            actions.map((action, index) => action)}
        </div>

        {!isIOSDevice && (
          <Popover
            id={volumePopoverId}
            open={openVolumePopover}
            anchorEl={anchorElVolume}
            onClose={handleVolumePopoverClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            PaperProps={{
              sx: {
                bgcolor: theme === 'dark' ? '#5C5A5E' : '#E1E1E5',
                borderRadius: '12px',
                p: '20px 12px',
                overflow: 'hidden',
                '@media (min-width: 640px)': {
                  borderRadius: '10px',
                  p: '16px 8px',
                },
              },
            }}
          >
            <Box
              sx={{ height: 120, '@media (min-width: 640px)': { height: 100 } }}
            >
              <Slider
                value={volume}
                max={1}
                step={0.01}
                onChange={handleVolumeChange}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                orientation="vertical"
                aria-labelledby="vertical-slider"
                sx={{
                  color: theme === 'dark' ? '#D4D5E6' : '#51526C',
                  '& .MuiSlider-thumb': {
                    width: 16,
                    height: 16,
                    bgcolor: theme === 'dark' ? '#D4D5E6' : '#51526C',
                    '@media (min-width: 640px)': {
                      width: 12,
                      height: 12,
                    },
                    '&:before': {
                      boxShadow: '0 2px 12px 0 rgba(0,0,0,0.2)',
                    },
                    '&:hover, &.Mui-focusVisible, &.Mui-active': {
                      boxShadow: '0px 0px 0px 8px rgba(81, 82, 108, 0.16)',
                    },
                  },
                  '& .MuiSlider-rail': {
                    bgcolor: '#B5B6C8',
                  },
                  '& .MuiSlider-track': {
                    width: 6,
                    '@media (min-width: 640px)': {
                      width: 4,
                    },
                  },
                }}
              />
            </Box>
          </Popover>
        )}

        <audio ref={audioRef} src={audioUrl} />
      </div>
    </div>
  );
};

export default AudioPlayer;
